import type { ReactNode } from 'react';
import { CheckOutlined, LoadingOutlined } from '@ant-design/icons';
import type { StatusStep, StepState } from '@/api/types';
import { useI18n } from '@/i18n/useI18n';
import { erasedOr } from '@/lib/erased';

const STATE: Record<StepState, { cls: string; icon: ReactNode }> = {
  DONE: { cls: 'dr-step--done', icon: <CheckOutlined aria-hidden="true" /> },
  ACTIVE: { cls: 'dr-step--active', icon: <LoadingOutlined aria-hidden="true" /> },
  PENDING: { cls: 'dr-step--pending', icon: <span aria-hidden="true" /> },
};

/**
 * A friendly vertical stepper of the case's progress. Step labels are server-provided (already
 * safe/plain — the backend guarantees tipping-off-safety); the state word is localized here. Built in
 * the DOM (not AntD Steps) for full control of the RTL-mirrored rail via CSS logical properties.
 */
export function StatusStepper({ steps }: { steps: StatusStep[] }) {
  const { t } = useI18n();
  return (
    <ol className="dr-steps">
      {steps.map((s, i) => {
        const m = STATE[s.state];
        return (
          <li key={`${i}-${s.label}`} className={`dr-step ${m.cls}`}>
            <span className="dr-step__marker">{m.icon}</span>
            <div className="dr-step__body">
              <div className="dr-step__label">{erasedOr(s.label, t('common.erased'))}</div>
              <div className="dr-step__state">{t(`status.state.${s.state}`)}</div>
            </div>
          </li>
        );
      })}
    </ol>
  );
}
